/**
 * Canvas-based image compression for chat attachments.
 *
 * Default: max 1280px longest side, JPEG quality 0.75 → ~100-300 KB from camera photos.
 * HD:      max 2560px, JPEG quality 0.92 → higher quality, larger file.
 * GIF:     returned as-is (preserves animation).
 */

const DEFAULT_MAX_SIZE = 1280;
const DEFAULT_QUALITY = 0.75;
const HD_MAX_SIZE = 2560;
const HD_QUALITY = 0.92;

/**
 * Compress an image file using Canvas API.
 *
 * @param {File|Blob} file - Source image
 * @param {Object} [opts]
 * @param {number} [opts.maxSize=1280] - Max pixels for longest side
 * @param {number} [opts.quality=0.75] - JPEG quality (0-1)
 * @returns {Promise<Blob>} Compressed JPEG blob (or original for GIF)
 */
export async function compressImage(file, opts = {}) {
  // GIF — return as-is (animation would be lost)
  if (file.type === 'image/gif') {
    return file;
  }

  const maxSize = opts.maxSize || DEFAULT_MAX_SIZE;
  const quality = opts.quality || DEFAULT_QUALITY;

  // Decode image
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;

  // Skip compression if already small enough
  if (width <= maxSize && height <= maxSize && file.size < 200 * 1024) {
    bitmap.close();
    return file;
  }

  // Calculate new dimensions (preserve aspect ratio)
  if (width > maxSize || height > maxSize) {
    const ratio = Math.min(maxSize / width, maxSize / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  // Draw on canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // Convert to JPEG blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        // If compression made it bigger (rare, small PNG), return original
        if (blob && blob.size < file.size) {
          resolve(blob);
        } else {
          resolve(file);
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/** Preset options for standard compression */
export const COMPRESS_DEFAULT = { maxSize: DEFAULT_MAX_SIZE, quality: DEFAULT_QUALITY };

/** Preset options for HD mode */
export const COMPRESS_HD = { maxSize: HD_MAX_SIZE, quality: HD_QUALITY };
